/**
 *
 * Sidebar
 *
 */
import * as React from "react"
import { NavLink as NavLinkRRD, Link, useNavigate } from "react-router-dom"
// reactstrap components
import {
  Collapse,
  NavbarBrand,
  Navbar,
  NavItem,
  NavLink,
  Nav,
  Container,
  Col,
} from "reactstrap"

import { Auth } from "../../../utils/auth"
import { removeLocalStorage } from "../../../utils/tokenstorage"

interface Props {
  location: any
  bgColor: String
  routes: any
  logo: any
}

export function Sidebar(props: Props) {
  const { bgColor, routes, logo } = props
  const [collapseOpen, setCollapseOpen] = React.useState<any>()
  const navigate = useNavigate()

  const handleLogout = () => {
    removeLocalStorage()
    navigate("/auth/login")
  }

  const activeRoute = (routeName: any) => {
    // Exact match for root path
    if (routeName === "/admin/" || routeName === "/admin") {
      return props.location.pathname === "/admin/" ||
        props.location.pathname === "/admin"
        ? "active"
        : ""
    }
    // For other paths, check if pathname starts with routeName
    return props.location.pathname === routeName ||
      props.location.pathname.startsWith(routeName + "/")
      ? "active"
      : ""
  }

  const toggleCollapse = () => {
    setCollapseOpen(data => !data)
  }

  const closeCollapse = () => {
    setCollapseOpen(false)
  }

  // creates the links that appear in the left menu / Sidebar
  //
  // Color treatment (2026-08-15) — matched to the approved Rider Ledger design
  // proposal: indigo-filled active item with a soft shadow,
  // muted grey resting state. Colors/weights only — Argon's structural
  // classes (navbar-vertical, fixed-left, etc.) and the real brand logo
  // below are untouched, so layout/positioning/behavior are unaffected.
  const createLinks = (routes: any) => {
    return routes.map((prop: any, key: any) => {
      // Use Auth.hasRole so role priority is respected (e.g. admin+rider won't see rider routes)
      // restrictToHub (opt-in per route) further narrows by hub name — can be
      // a single hub name or an array of allowed hub names (e.g. Transmittal:
      // ["Manila", "Mandaluyong"]). Exempts ONLY super_admin (the one truly
      // company-wide, no-hub tier). admin is deliberately NOT exempted here
      // even though it's company-wide elsewhere in this codebase — this
      // restriction is specific to hub-scoped features, so an admin assigned
      // to an out-of-scope hub must be gated exactly like a hub_admin would
      // be, matched by their own hub name. An admin genuinely based in an
      // allowed hub still gets through via that same match, same as any
      // hub_admin at that hub would.
      const hasPermission =
        prop.permissions &&
        prop.permissions.some((role: string) => Auth.hasRole(role)) &&
        (!prop.restrictToHub ||
          Auth.hasRole("super_admin") ||
          (Array.isArray(prop.restrictToHub)
            ? prop.restrictToHub.includes(Auth.hubname())
            : Auth.hubname() === prop.restrictToHub))
      if (prop.layout === "/auth" || !hasPermission) return
      const isActive = activeRoute(prop.layout + prop.path) === "active"
      return (
        <NavItem key={key}>
          <NavLink
            to={prop.layout + prop.path}
            tag={NavLinkRRD}
            onClick={closeCollapse}
            className={isActive ? "active" : ""}
            style={{
              borderRadius: "8px",
              margin: "4px 8px",
              padding: "12px 16px",
              transition: "all 0.2s ease",
              fontWeight: isActive ? 600 : 500,
              background: isActive ? "#5e72e4" : "transparent",
              color: isActive ? "#ffffff" : "#8898aa",
              boxShadow: isActive ? "0 6px 14px rgba(94,114,228,0.35)" : "none",
            }}
          >
            <i
              className={prop.icon}
              style={{
                marginRight: "12px",
                fontSize: "18px",
                color: isActive ? "#ffffff" : "#8898aa",
                opacity: isActive ? 1 : 0.75,
              }}
            />
            {prop.name}
          </NavLink>
        </NavItem>
      )
    })
  }

  let navbarBrandProps: any
  if (logo && logo.innerLink) {
    navbarBrandProps = {
      to: logo.innerLink,
      tag: Link,
    }
  } else if (logo && logo.outterLink) {
    navbarBrandProps = {
      href: logo.outterLink,
      target: "_blank",
    }
  }

  return (
    <Navbar
      className="navbar-vertical fixed-left navbar-light bg-white"
      expand="md"
      id="sidenav-main"
      style={{ borderRight: "1px solid #e9ecef" }}
    >
      <Container fluid>
        {/* Toggler */}
        <button
          className="navbar-toggler"
          type="button"
          onClick={toggleCollapse}
        >
          <span className="navbar-toggler-icon" />
        </button>
        {/* Brand */}
        {logo ? (
          <NavbarBrand className="pt-0" {...navbarBrandProps}>
            <img
              alt={logo.imgAlt}
              className="navbar-brand-img"
              src={logo.imgSrc}
            />
          </NavbarBrand>
        ) : null}
        {/* User */}
        {/* Collapse */}
        <Collapse navbar isOpen={collapseOpen}>
          <div className="navbar-collapse-header d-md-none">
            <Col className="collapse-close" xs="6">
              <button className="navbar-toggler" type="button">
                <span />
              </button>
            </Col>
          </div>
          {/* Navigation */}
          <Nav navbar>{createLinks(routes)}</Nav>

          {/* User info + logout — visible on mobile sidebar (hidden on desktop where navbar handles it) */}
          <div
            className="d-md-none"
            style={{
              borderTop: "1px solid #e9ecef",
              marginTop: "8px",
              paddingTop: "16px",
              paddingBottom: "8px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "4px 16px 12px",
              }}
            >
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  flexShrink: 0,
                  background:
                    "linear-gradient(135deg, #f5365c 0%, #d63031 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: "13px",
                  fontWeight: 700,
                }}
              >
                {(Auth.username() || "U").substring(0, 2).toUpperCase()}
              </div>
              <div>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: "#32325d",
                  }}
                >
                  {Auth.username()}
                </div>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#8898aa",
                    textTransform: "capitalize",
                  }}
                >
                  {Auth.role()}
                </div>
              </div>
            </div>
            <NavItem>
              <NavLink
                href="#"
                onClick={e => {
                  e.preventDefault()
                  handleLogout()
                }}
                style={{
                  borderRadius: "8px",
                  margin: "0 8px",
                  padding: "12px 16px",
                  color: "#f5365c",
                  fontWeight: 500,
                }}
              >
                <i
                  className="ni ni-user-run"
                  style={{ marginRight: "12px", fontSize: "18px" }}
                />
                Logout
              </NavLink>
            </NavItem>
          </div>
        </Collapse>
      </Container>
    </Navbar>
  )
}
